import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom"; // Import Link
import GenericModal from "./GenericModal";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import {
  LockClosedIcon,
  AtSymbolIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { axiosInstance } from "../../lib/axiosInstance";

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.9 0 6.7 1.6 8.3 3.1l6.3-6.3C34.9 2.7 30 .7 24 .7 14.8.7 7.2 6.3 4.4 14.3l7.3 5.7C13.3 13.1 18.2 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.3 24.5c0-1.6-.1-3.1-.4-4.6H24v8.8h12.5c-.5 2.8-2.2 5.2-4.9 6.9l7.3 5.7c4.3-3.9 6.8-9.7 6.8-16.8z"
    />
    <path
      fill="#FBBC05"
      d="M11.7 28.1c-.4-1.2-.6-2.5-.6-3.9s.2-2.7.6-3.9l-7.3-5.7C2.7 18.1 1.7 21 1.7 24.2s1 6.1 4.7 9.8l7.3-5.9z"
    />
    <path
      fill="#34A853"
      d="M24 47.3c6.5 0 11.9-2.1 15.8-5.7l-7.3-5.7c-2.1 1.4-4.9 2.3-8.5 2.3-5.9 0-10.9-3.9-12.7-9.2l-7.3 5.7C7.2 41 14.8 47.3 24 47.3z"
    />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
  const [credential, setCredential] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { customerLogin } = useAuthStore();

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setLoading(false);
        setGoogleLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredential({ ...credential, [name]: value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const success = await customerLogin(credential);
      if (success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Login from modal failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginClick = async () => {
    setGoogleLoading(true);
    try {
      // --- MODIFICATION START ---
      sessionStorage.setItem('googleAuthStatus', 'in-progress');
      sessionStorage.setItem(
        "googleAuthRedirect",
        window.location.pathname + window.location.search
      );
      // --- MODIFICATION END ---
      const response = await axiosInstance.get("/customer/google/url");
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Could not get Google login URL.");
        setGoogleLoading(false);
        // --- MODIFICATION START ---
        sessionStorage.removeItem('googleAuthStatus');
        sessionStorage.removeItem('googleAuthRedirect');
        // --- MODIFICATION END ---
      }
    } catch (error) {
      toast.error("Error initiating Google login.");
      console.error("Google URL fetch error:", error);
      setGoogleLoading(false);
      // --- MODIFICATION START ---
      sessionStorage.removeItem('googleAuthStatus');
      sessionStorage.removeItem('googleAuthRedirect');
      // --- MODIFICATION END ---
    }
  };

  const handleClose = () => {
    setCredential({ email: "", password: "" });
    setLoading(false);
    setShowPassword(false);
    onClose();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Customer Login"
      size="max-w-md"
    >
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email
          </label>
          <div className="relative">
            <AtSymbolIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              id="email"
              name="email"
              value={credential.email}
              onChange={handleInputChange}
              className="w-full pl-10 pr-3 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Password
          </label>
          <div className="relative">
            <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={credential.password}
              onChange={handleInputChange}
              className="w-full pl-10 pr-10 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div className="text-right text-sm">
          <Link
            to="/customer/forgot-password"
            className="font-medium text-blue-600 hover:underline"
            onClick={handleClose}
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={loading || googleLoading}
          className="w-full bg-blue-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center disabled:opacity-60"
        >
          {loading ? (
            <AiOutlineLoading3Quarters className="animate-spin text-xl" />
          ) : (
            "Login"
          )}
        </button>
        <div className="flex items-center my-3">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        <button
          type="button"
          onClick={handleGoogleLoginClick}
          disabled={loading || googleLoading}
          className="w-full border flex items-center justify-center gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400 hover:text-slate-900 hover:shadow transition duration-150 py-2.5 disabled:opacity-60"
        >
          {googleLoading ? (
            <AiOutlineLoading3Quarters className="animate-spin text-xl" />
          ) : (
            <>
              <GoogleIcon />
              <span>Login with Google</span>
            </>
          )}
        </button>
      </form>
    </GenericModal>
  );
};

export default LoginModal;